import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import axios, { CancelToken } from "axios";
import DataTable from "../../../../../components/dataTable/DataTable";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { showError, showSuccess } from "redux/actions/ui";
import moment from "moment";
import {
  GetInventoryCount,
  PostInventoryCount,
  PutPrintInventoryCount,
  TInventoryCount,
} from "api/rpc/2024-04/facilityAdmin/product/inventoryCount";
import { StatusCode } from "api/protocols";
import { openPrintWindow, valueToString } from "helpers/Helpers";
import { getCountsBadgeType } from "./ViewInventoryCount";
import { Badge } from "components/badge/Badge";
import _ from "lodash";
import Toggle from "components/form/toggle/Toggle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/pro-regular-svg-icons";

interface IState {
  inventoryCounts: Array<TInventoryCount>;
  newCountsPopup: boolean;
}

interface IFilterState {
  limit: number;
  offset: number;
}

export default function InventoryCounts() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [state, setState] = useState<IState>({
    inventoryCounts: undefined,
    newCountsPopup: false,
  });

  const [newCountScope, setNewCountScope] = useState<boolean>(false); // false for partial true for full

  const [filterState, setFilterState] = useState<IFilterState>({
    limit: 50,
    offset: 0,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadInventoryCounts(source.token);
    return () => {
      source.cancel();
    };
  }, [filterState.offset]);

  async function loadInventoryCounts(cancelToken: CancelToken) {
    const countsRes = await GetInventoryCount(
      { offset: filterState.offset, limit: filterState.limit },
      false,
      cancelToken,
    );
    if (countsRes.status !== StatusCode.OK) {
      if (cancelToken.reason) {
        return;
      }
      dispatch(showError(t("Error getting inventory counts")));
      return;
    }
    setState(prevState => ({ ...prevState, inventoryCounts: countsRes.data }));
  }

  async function createCountRecord() {
    const countsRes = await PostInventoryCount({ scope: newCountScope ? "full" : "partial" }, true);
    if (countsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating inventory count record")));
      return;
    }
    dispatch(showSuccess(t("Successfully created inventory count record")));
    setState(prevState => ({ ...prevState, newCountsPopup: false }));
    history.push(`/admin/settings/counts/new/${countsRes?.data?.id}`, {
      params: {
        inventoryCount: countsRes.data,
      },
    });
  }

  async function handlePrintCount(e: React.MouseEvent<SVGSVGElement, MouseEvent>, id: number) {
    e.stopPropagation();
    const res = await PutPrintInventoryCount({ inventory_count_id: Number(id) }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing selected inventory count")));
      return;
    }

    void openPrintWindow(res?.data as string);
  }

  function navigateToViewPage(id: number) {
    history.push(`/admin/settings/counts/view/${id}`);
  }

  return (
    <Page
      title={t("Inventory Counts")}
      narrow
      primaryAction={{
        content: t("New Count"),
        action: () => setState(prevState => ({ ...prevState, newCountsPopup: true })),
      }}
    >
      <DataTable
        columns={[
          { label: t("Status") },
          { label: t("Scope") },
          { label: t("Created By") },
          { label: t("Date Created") },
          { label: t("Date Submitted") },
          { label: "" },
        ]}
        footer={{
          tableLimit: filterState.limit,
          tableOffset: filterState.offset,
          disableNextOffset: !state.inventoryCounts || !(state.inventoryCounts?.length === filterState.limit),
          handleTableOffset: direction =>
            setFilterState(prev => ({
              ...prev,
              offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
            })),
        }}
        loading={!state.inventoryCounts}
      >
        {state.inventoryCounts?.map((count, index) => {
          return (
            <tr key={index} className="clickable" onClick={() => navigateToViewPage(count?.id)}>
              <td>
                {" "}
                <Badge type={getCountsBadgeType(count?.status)}>{valueToString(count?.status)}</Badge>
              </td>
              <td>
                <Badge type={count?.scope === "partial" ? "gray" : "lightblue"}>{_.startCase(count?.scope)}</Badge>
              </td>
              <td>{count?.created_by_full_name}</td>
              <td>{moment(count.date_created, "YYYY-MM-DD h:mm:ss").format("LL")}</td>
              <td>{count.date_submitted && moment(count.date_submitted, "YYYY-MM-DD h:mm:ss").format("LL")}</td>
              <td>{<FontAwesomeIcon icon={faPrint} onClick={e => handlePrintCount(e, count?.id)} />}</td>
            </tr>
          );
        })}
      </DataTable>

      <Portal isMounted={state.newCountsPopup}>
        <Popup
          open={state.newCountsPopup}
          type="info"
          onOk={() => createCountRecord()}
          onCancel={() => setState(prevState => ({ ...prevState, newCountsPopup: false }))}
          okText={t("Add")}
          title={t("Add New Inventory Count Record?")}
          description={t("Inventory can be counted and saved before finalized.")}
        >
          <div className="font-normal text-sm mb-4">{t("")}</div>
          <Toggle
            labelTop={t("Full Inventory Count")}
            tooltip={t("In a full inventory count, all products not counted in the list will be adjusted to 0.")}
            checked={newCountScope}
            onChange={() => setNewCountScope(!newCountScope)}
          />
        </Popup>
      </Portal>
    </Page>
  );
}
